/**
 * UsbPrinterFields — Conexión USB de la impresora térmica (desktop: puertos
 * Windows; APK: escáner USB OTG nativo; web: WebUSB vía relay de permisos).
 *
 * QUÉ EXPORTA: UsbPrinterFields (named).
 */

import { AlertTriangle, Loader2, Lock, RefreshCw, Usb } from "lucide-react";
import toast from "react-hot-toast";
import type { PosPrinterConfig } from "@/lib/native/posPrinterConfig";
import { POS_HARDWARE_SECTION_LABEL } from "../utils/userSettingsPosHardware.helpers";

export interface PosUsbDeviceInfo {
  name: string;
  vendorId: number;
  productId: number;
  deviceKey: string;
  hasPermission?: boolean;
}

interface UsbPrinterFieldsProps {
  cfg: PosPrinterConfig;
  onChange: (patch: Partial<PosPrinterConfig>) => void;
  isDesktop: boolean;
  isNative: boolean;
  usbPorts: string[];
  usbPortsLoading: boolean;
  onRefreshUsbPorts: () => void;
  usbDevices: PosUsbDeviceInfo[];
  usbLoading: boolean;
  usbPermBusy: boolean;
  onRefreshUsb: () => void;
  /** Devuelve true si Android concedió el permiso (no-op fuera de APK). */
  onRequestUsbAccess: (deviceKey?: string) => Promise<boolean>;
}

export function UsbPrinterFields({
  cfg,
  onChange,
  isDesktop,
  isNative,
  usbPorts,
  usbPortsLoading,
  onRefreshUsbPorts,
  usbDevices,
  usbLoading,
  usbPermBusy,
  onRefreshUsb,
  onRequestUsbAccess,
}: UsbPrinterFieldsProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-slate-500">
          {isDesktop
            ? "Impresora USB (escritorio)"
            : "Impresoras USB conectadas"}
        </p>
        {!isDesktop && (
          <button
            type="button"
            onClick={onRefreshUsb}
            disabled={usbLoading}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary-600 text-white text-[10px] font-bold disabled:opacity-50"
          >
            {usbLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <RefreshCw size={12} />
            )}
            Buscar
          </button>
        )}
      </div>
      <div className="flex items-start gap-2 p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600">
        <Usb
          size={14}
          className="text-slate-600 dark:text-slate-300 shrink-0 mt-0.5"
        />
        {isDesktop ? (
          <p className="text-[10px] text-slate-700 dark:text-slate-200 leading-snug">
            Conecta la impresora por <strong>USB</strong> al PC. Pulsa{" "}
            <strong>Buscar</strong> y elige el puerto <strong>USB001</strong>{" "}
            (no puertos custom del driver). Si la impresora no aparece,
            instálala en Windows y vuelve a buscar.
          </p>
        ) : (
          <p className="text-[10px] text-slate-700 dark:text-slate-200 leading-snug">
            Conecta la térmica con cable <strong>USB OTG</strong>. Pulsa{" "}
            <strong>Buscar</strong>, elige la impresora y toca{" "}
            <strong>Conectar</strong> — ahí Android pedirá permiso (Aceptar).
            Funciona <strong>sin internet</strong>.
          </p>
        )}
      </div>
      {/* Desktop: auto-detección + input manual de puerto USB */}
      {isDesktop && (
        <div className="space-y-2">
          {/* Banner migración: clave legacy WebUSB (vendorId:productId) */}
          {/^\d+:\d+$/.test(cfg.usbDeviceKey) && (
            <div className="flex items-start gap-2 p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700">
              <AlertTriangle
                size={14}
                className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-amber-900 dark:text-amber-100 leading-snug">
                  El valor guardado <strong>({cfg.usbDeviceKey})</strong> es un
                  ID de WebUSB, no un puerto Windows. Limpia y pulsa{" "}
                  <strong>Buscar puertos USB</strong>.
                </p>
                <button
                  type="button"
                  onClick={() => onChange({ usbDeviceKey: "" })}
                  className="mt-1.5 px-2.5 py-1 rounded-lg bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 text-[10px] font-bold hover:bg-amber-300 dark:hover:bg-amber-700 transition-colors"
                >
                  Limpiar
                </button>
              </div>
            </div>
          )}

          {/* Botón de auto-detección + sección de puertos encontrados */}
          <div className="flex items-center justify-between gap-2">
            <p className={POS_HARDWARE_SECTION_LABEL}>
              Puertos USB disponibles
            </p>
            <button
              type="button"
              onClick={onRefreshUsbPorts}
              disabled={usbPortsLoading}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary-600 text-white text-[10px] font-bold disabled:opacity-50"
            >
              {usbPortsLoading ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              Buscar
            </button>
          </div>

          <div className="flex items-start gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-600">
            <Usb
              size={14}
              className="text-slate-500 dark:text-slate-400 shrink-0 mt-0.5"
            />
            <p className="text-[10px] text-slate-700 dark:text-slate-200 leading-snug">
              Pulsa <strong>Buscar</strong> para detectar automáticamente los
              puertos USB de la impresora. La impresora debe estar{" "}
              <strong>conectada e instalada</strong> en Windows (aparece en
              Dispositivos e impresoras del Panel de control).
            </p>
          </div>

          {/* Lista de puertos encontrados */}
          {usbPorts.length > 0 && (
            <ul className="max-h-40 overflow-y-auto space-y-1 custom-scrollbar">
              {usbPorts.map((port) => {
                const sel = cfg.usbDeviceKey === port;
                return (
                  <li key={port}>
                    <button
                      type="button"
                      onClick={() => onChange({ usbDeviceKey: port })}
                      className={`w-full text-left px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all ${
                        sel
                          ? "border-primary-600 bg-primary-50 dark:bg-primary-950/40 text-primary-800 dark:text-primary-200"
                          : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                      }`}
                    >
                      <span className="font-mono">{port}</span>
                      {sel && (
                        <span className="ml-2 text-[9px] font-black uppercase tracking-wide text-primary-600">
                          Seleccionado
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {usbPorts.length === 0 && !usbPortsLoading && (
            <p className="text-[11px] text-gray-500 dark:text-slate-400 px-0.5">
              Pulsa «Buscar» con la impresora USB conectada. Si no aparece,
              instálala en{" "}
              <strong>Dispositivos e impresoras → Agregar impresora</strong>.
            </p>
          )}

          {/* Input manual como fallback */}
          <div className="relative">
            <Usb size={16} className="abs-center-y left-3 text-gray-400" />
            <input
              type="text"
              autoComplete="off"
              placeholder="Puerto manual (ej. USB001)"
              value={cfg.usbDeviceKey}
              onChange={(e) =>
                onChange({ usbDeviceKey: e.target.value.trim().toUpperCase() })
              }
              className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-mono"
            />
          </div>
          <p className="px-0.5 text-[10px] text-gray-500 dark:text-slate-400 leading-snug">
            Si la auto-detección no funciona, busca el nombre en{" "}
            <strong>
              Panel de control → Dispositivos → clic derecho → Propiedades →
              Puertos
            </strong>
            . Suele ser <strong>USB001</strong>.
          </p>
        </div>
      )}

      {/* Android: escáner USB OTG nativo */}
      {isNative && usbDevices.length === 0 && (
        <p className="text-[11px] text-gray-500 dark:text-slate-400 px-0.5">
          Pulsa «Buscar» con la impresora conectada, elige una de la lista y
          pulsa «Conectar». Luego guarda la configuración.
        </p>
      )}
      {isNative && usbDevices.length > 0 && (
        <ul className="max-h-40 overflow-y-auto space-y-1 custom-scrollbar">
          {usbDevices.map((d) => {
            const sel = cfg.usbDeviceKey === d.deviceKey;
            return (
              <li key={d.deviceKey}>
                <button
                  type="button"
                  onClick={() => {
                    void (async () => {
                      if (d.hasPermission === false) {
                        const ok = await onRequestUsbAccess(d.deviceKey);
                        if (!ok) return;
                        await onRefreshUsb();
                      }
                      onChange({
                        usbDeviceKey: d.deviceKey,
                        deviceName: cfg.deviceName || d.name,
                      });
                      toast.success(
                        d.hasPermission !== false
                          ? `${d.name} conectada`
                          : "Permiso concedido — impresora conectada",
                      );
                    })();
                  }}
                  disabled={usbPermBusy}
                  className={`w-full text-left px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all disabled:opacity-60 ${
                    sel
                      ? "border-primary-600 bg-primary-50 dark:bg-primary-950/40 text-primary-800 dark:text-primary-200"
                      : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="block font-bold">{d.name}</span>
                    {!sel && d.hasPermission === false && (
                      <span className="shrink-0 text-[9px] font-black uppercase tracking-wide text-amber-700 dark:text-amber-300 flex items-center gap-1">
                        <Lock size={10} />
                        Conectar
                      </span>
                    )}
                    {sel && (
                      <span className="shrink-0 text-[9px] font-black uppercase tracking-wide text-primary-600">
                        Conectada
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] font-mono text-gray-500">
                    {d.deviceKey}
                    {d.hasPermission === false ? " · sin permiso" : " · listo"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
